import React, { memo, ReactElement, useMemo, useCallback } from 'react'
import { $enum } from 'ts-enum-util'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IStop, ILoadStatusOption } from 'apps/fleet-planner/types'
import {
  getLoadStatusOptionValue,
  filterPickupStops,
  filterDropoffStops,
  getOptions,
} from 'apps/fleet-planner/utils/loadStatusTags'
import { IFunctionalTagOption, FunctionalTag, TAG_SIZE } from 'transfix-ui'

export interface ILoadStatusFunctionalTagProps {
  status: LOAD_STATE
  currentStopSequence?: number
  stops: IStop[]
  dataTest?: string
  isLoading?: boolean
  onChange: (newStatus: ILoadStatusOption) => void
}

export const LoadStatusFunctionalTag = memo<ILoadStatusFunctionalTagProps>(
  ({
    status,
    currentStopSequence = 0,
    onChange,
    stops = [],
    dataTest = 'load-status',
    isLoading,
  }): ReactElement => {
    const { trackEvent } = useFMSAnalytics()

    const currentStatusValue = useMemo(
      () =>
        getLoadStatusOptionValue(
          $enum(LOAD_STATE).getKeyOrDefault(status, 'Pending'),
          currentStopSequence
        ),
      [status, currentStopSequence]
    )

    const pickupStops = useMemo(() => filterPickupStops(stops), [stops])
    const dropoffStops = useMemo(() => filterDropoffStops(stops), [stops])
    const options = useMemo(() => getOptions({ pickupStops, dropoffStops }), [
      pickupStops,
      dropoffStops,
    ])

    const updateStatusHandler = useCallback(
      (selectedOption: IFunctionalTagOption<string>) => {
        const [, updatedStatus, stopSequence] = selectedOption.value.split(':') as [
          string,
          LOAD_STATE,
          string
        ]
        onChange({ status: updatedStatus, currentStopSequence: stopSequence })
        trackEvent('Load', 'CHANGED_LOAD_STATUS')
      },
      [onChange]
    )

    return (
      <div className='fp-status-select flex items-center'>
        <FunctionalTag
          options={options}
          value={currentStatusValue}
          onSelect={updateStatusHandler}
          dataTest={dataTest}
          isLoading={isLoading}
          size={TAG_SIZE.small}
        />
      </div>
    )
  }
)
